import React, { useState } from 'react';
import styled from 'styled-components';
import { Key, Globe, Folder, Palette, TestTube, Save, RotateCcw, Check, X, Type } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import GoogleTranslateService from '../services/googleTranslateService';

const PageContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.background};
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.theme.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.primary};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.text};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const Select = styled.select`
  width: 100%;
  max-width: 400px;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.primary}20;
  }
`;

const ApiKeyGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: end;
  margin-bottom: 16px;
`;

const TestButton = styled.button`
  padding: 10px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TestResult = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  margin-top: 4px;
  color: ${props => props.success ? props.theme.success : props.theme.danger};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${props => props.theme.border};
`;

const Button = styled.button`
  padding: 12px 20px;
  border: 1px solid ${props => props.variant === 'primary' ? props.theme.primary : props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ThemeToggle = styled.div`
  display: flex;
  align-items: center;
`;



const ToggleSwitch = styled.button`
  width: 48px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? props.theme.primary : props.theme.border};
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.active ? '26px' : '2px'};
    transition: all 0.2s ease;
  }
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text};
`;

const SettingsPage = ({ toggleTheme, isDarkMode }) => {
  const { 
    apiKeys, 
    preferences, 
    updateApiKey, 
    updatePreference, 
    testApiKey, 
    saveSettings,
    resetSettings 
  } = useSettings();
  
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  


  const handleApiKeyChange = (service, value) => {
    updateApiKey(service, value);
  };

  const handlePreferenceChange = (key, value) => {
    updatePreference(key, value);
  };

  const handleTestApiKey = async (service) => {
    setTesting({ ...testing, [service]: true });
    
    try {
      const result = await testApiKey(service);
      setTestResults({ ...testResults, [service]: result });
    } catch (error) {
      setTestResults({ 
        ...testResults, 
        [service]: { success: false, message: error.message } 
      });
    } finally {
      setTesting({ ...testing, [service]: false });
    }
  };

  const handleSelectExportPath = async () => {
    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      // Use 'select-folder' which is more standard across the app
      const path = await ipcRenderer.invoke('select-folder', {
        title: 'Select Default Export Directory',
        defaultPath: preferences.defaultExportPath
      });
      if (path) {
        handlePreferenceChange('defaultExportPath', path);
      }
    } else {
      // Browser fallback - fetch from Local Agent select-folder
      try {
        const response = await fetch('http://localhost:8001/select-folder?title=Select Default Export Directory');
        const data = await response.json();
        if (data && data.path) {
          handlePreferenceChange('defaultExportPath', data.path);
        }
      } catch (error) {
        console.error('❌ SettingsPage: Failed to open folder picker via Local Agent:', error);
        // Fallback to prompt if Local Agent is not running
        const userEnteredPath = prompt('Enter the default export directory path:\n(Make sure the folder exists on your computer)', preferences.defaultExportPath || 'C:\\Users\\Inuobal\\Downloads');
        if (userEnteredPath) {
          handlePreferenceChange('defaultExportPath', userEnteredPath.trim());
        }
      }
    }
  };

  const handleSave = async () => {
    try {
      await saveSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      try {
        await resetSettings();
        setTestResults({});
      } catch (error) {
        console.error('Error resetting settings:', error);
      }
    }
  };



  return (
    <PageContainer>
      <Header>
        <Title>Settings</Title>
        <Subtitle>Configure API keys, fonts, and application preferences</Subtitle>
      </Header>

      <Content>
        {/* API Keys Section */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Key size={16} />
            </SectionIcon>
            <div>
              <SectionTitle>API Keys</SectionTitle>
              <SectionDescription>Configure external service API keys for transcription and translation</SectionDescription>
            </div>
          </SectionHeader>

          <FormGroup>
            <Label>OpenAI Whisper API Key</Label>
            <ApiKeyGroup>
              <Input
                type="password"
                placeholder="sk-..."
                value={apiKeys.whisper}
                onChange={(e) => handleApiKeyChange('whisper', e.target.value)}
              />
              <TestButton
                onClick={() => handleTestApiKey('whisper')}
                disabled={!apiKeys.whisper || testing.whisper}
              >
                <TestTube size={14} />
                {testing.whisper ? 'Testing...' : 'Test'}
              </TestButton>
            </ApiKeyGroup>
            {testResults.whisper && (
              <TestResult success={testResults.whisper.success}>
                {testResults.whisper.success ? <Check size={14} /> : <X size={14} />}
                {testResults.whisper.message}
              </TestResult>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Google Translate API Key</Label>
            <ApiKeyGroup>
              <Input
                type="password"
                placeholder="AIza..."
                value={apiKeys.googleTranslate}
                onChange={(e) => handleApiKeyChange('googleTranslate', e.target.value)}
              />
              <TestButton
                onClick={() => handleTestApiKey('googleTranslate')}
                disabled={!apiKeys.googleTranslate || testing.googleTranslate}
              >
                <TestTube size={14} />
                {testing.googleTranslate ? 'Testing...' : 'Test'}
              </TestButton>
            </ApiKeyGroup>
            {testResults.googleTranslate && (
              <TestResult success={testResults.googleTranslate.success}>
                {testResults.googleTranslate.success ? <Check size={14} /> : <X size={14} />}
                {testResults.googleTranslate.message}
              </TestResult>
            )}
          </FormGroup>

          <FormGroup>
            <Label>DeepL API Key</Label>
            <ApiKeyGroup>
              <Input
                type="password"
                placeholder="..."
                value={apiKeys.deepL}
                onChange={(e) => handleApiKeyChange('deepL', e.target.value)}
              />
              <TestButton
                onClick={() => handleTestApiKey('deepL')}
                disabled={!apiKeys.deepL || testing.deepL}
              >
                <TestTube size={14} />
                {testing.deepL ? 'Testing...' : 'Test'}
              </TestButton>
            </ApiKeyGroup>
            {testResults.deepL && (
              <TestResult success={testResults.deepL.success}>
                {testResults.deepL.success ? <Check size={14} /> : <X size={14} />}
                {testResults.deepL.message}
              </TestResult>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Pyannote Speaker Diarization API Key (Legacy)</Label>
            <ApiKeyGroup>
              <Input
                type="password"
                placeholder="sk_..."
                value={apiKeys.pyannote}
                onChange={(e) => handleApiKeyChange('pyannote', e.target.value)}
              />
              <TestButton
                onClick={() => handleTestApiKey('pyannote')}
                disabled={!apiKeys.pyannote || testing.pyannote}
              >
                <TestTube size={14} />
                {testing.pyannote ? 'Testing...' : 'Test'}
              </TestButton>
            </ApiKeyGroup>
            {testResults.pyannote && (
              <TestResult success={testResults.pyannote.success}>
                {testResults.pyannote.success ? <Check size={14} /> : <X size={14} />}
                {testResults.pyannote.message}
              </TestResult>
            )}
          </FormGroup>

          <FormGroup>
            <Label>🎤 Hugging Face API Token (Recommended)</Label>
            <ApiKeyGroup>
              <Input
                type="password"
                placeholder="hf_..."
                value={apiKeys.huggingface}
                onChange={(e) => handleApiKeyChange('huggingface', e.target.value)}
              />
              <TestButton
                onClick={() => handleTestApiKey('huggingface')}
                disabled={!apiKeys.huggingface || testing.huggingface}
              >
                <TestTube size={14} />
                {testing.huggingface ? 'Testing...' : 'Test'}
              </TestButton>
            </ApiKeyGroup>
            {testResults.huggingface && (
              <TestResult success={testResults.huggingface.success}>
                {testResults.huggingface.success ? <Check size={14} /> : <X size={14} />}
                {testResults.huggingface.message}
              </TestResult>
            )}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              ✅ No Python installation required • Free tier available • Get token at <a href="https://hf.co/settings/tokens" target="_blank" rel="noopener noreferrer">hf.co/settings/tokens</a>
            </div>
          </FormGroup>
        </Section>

        {/* Subtitle Fonts Section */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Type size={16} />
            </SectionIcon>
            <div>
              <SectionTitle>Subtitle Fonts</SectionTitle>
              <SectionDescription>
                Choose default fonts for subtitles. These settings are used as defaults for new projects and exports.
              </SectionDescription>
            </div>
          </SectionHeader>

          <FormGroup>
            <Label>Default Subtitle Font (Latin / English)</Label>
            <Input
              type="text"
              placeholder="Arial"
              value={preferences.subtitleFontFamily || ''}
              onChange={(e) => handlePreferenceChange('subtitleFontFamily', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Arabic Subtitle Font Family (Name)</Label>
            <Input
              type="text"
              placeholder="Bahij TheSansArabic"
              value={preferences.subtitleArabicFontFamily || ''}
              onChange={(e) => handlePreferenceChange('subtitleArabicFontFamily', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Arabic Font File Path (for export)</Label>
            <Input
              type="text"
              placeholder="/fonts/bahij-thesansarabic-bold.ttf"
              value={preferences.subtitleArabicFontPath || ''}
              onChange={(e) => handlePreferenceChange('subtitleArabicFontPath', e.target.value)}
            />
            <SectionDescription style={{ marginTop: 4 }}>
              You already added <code>bahij-thesansarabic-bold.ttf</code> to <code>public/fonts</code>. The default path
              here points to that file.
            </SectionDescription>
          </FormGroup>
        </Section>

        {/* Language & Localization */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Globe size={16} />
            </SectionIcon>
            <div>
              <SectionTitle>Language & Localization</SectionTitle>
              <SectionDescription>Set default language preferences</SectionDescription>
            </div>
          </SectionHeader>

          <FormGroup>
            <Label>Default Language</Label>
            <Select
              value={preferences.defaultLanguage}
              onChange={(e) => handlePreferenceChange('defaultLanguage', e.target.value)}
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="it">Italian</option>
              <option value="pt">Portuguese</option>
              <option value="ru">Russian</option>
              <option value="ja">Japanese</option>
              <option value="ko">Korean</option>
              <option value="zh">Chinese</option>
            </Select>
          </FormGroup>
        </Section>

        {/* Export Settings */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Folder size={16} />
            </SectionIcon>
            <div>
              <SectionTitle>Export Settings</SectionTitle>
              <SectionDescription>Configure default export preferences</SectionDescription>
            </div>
          </SectionHeader>

          <FormGroup>
            <Label>Default Export Directory</Label>
            <ApiKeyGroup>
              <Input
                type="text"
                placeholder="C:\Users\...\Exports"
                value={preferences.defaultExportPath || ''}
                onChange={(e) => handlePreferenceChange('defaultExportPath', e.target.value)}
              />
              <TestButton onClick={handleSelectExportPath}>
                <Folder size={14} />
                Browse
              </TestButton>
            </ApiKeyGroup>
            <SectionDescription style={{ marginTop: 4 }}>
              This directory will be used as the default location for all video exports.
            </SectionDescription>
          </FormGroup>

          <FormGroup>
            <Label>Default Resolution</Label>
            <Select
              value={preferences.defaultResolution}
              onChange={(e) => handlePreferenceChange('defaultResolution', e.target.value)}
            >
              <option value="720p">720p (HD)</option>
              <option value="1080p">1080p (Full HD)</option>
              <option value="1440p">1440p (2K)</option>
              <option value="2160p">2160p (4K)</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Default Video Format</Label>
            <Select
              value={preferences.defaultFormat}
              onChange={(e) => handlePreferenceChange('defaultFormat', e.target.value)}
            >
              <option value="16:9">16:9 (YouTube/Landscape)</option>
              <option value="9:16">9:16 (Shorts/Portrait)</option>
              <option value="1:1">1:1 (Instagram Square)</option>
              <option value="4:5">4:5 (Instagram Portrait)</option>
            </Select>
          </FormGroup>


        </Section>



        {/* Appearance */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Palette size={16} />
            </SectionIcon>
            <div>
              <SectionTitle>Appearance</SectionTitle>
              <SectionDescription>Customize the application appearance</SectionDescription>
            </div>
          </SectionHeader>

          <FormGroup>
            <Label>Theme</Label>
            <ThemeToggle>
              <ToggleLabel>Light</ToggleLabel>
              <ToggleSwitch active={isDarkMode} onClick={toggleTheme} />
              <ToggleLabel>Dark</ToggleLabel>
            </ThemeToggle>
          </FormGroup>
        </Section>

        <ButtonGroup>
          <Button variant="primary" onClick={handleSave}>
            <Save size={16} />
            Save Settings
          </Button>
          <Button onClick={handleReset}>
            <RotateCcw size={16} />
            Reset to Defaults
          </Button>
        </ButtonGroup>
      </Content>
    </PageContainer>
  );
};

export default SettingsPage;